// profiles.store.js
import { observable, computed, action, runInAction, makeObservable } from "mobx";

import { axios } from "../../utils/axios";

// const { NotificationManager } = require("react-notifications");
import { v4 as uuidv4 } from "uuid";

import {
    QuizObject,
    QuizObjectTable,
    QuizObjectCardTable
} from "./quiz_objects/quiz-object";

import FetchedQuizObject from "./quiz_objects/fetched_quiz_object";

export default class QuizObjectsStore {
    @observable quiz_objects = new Map();
    @observable quiz_object = {};
    @observable state = "pending"; // "pending" / "done" / "error"
    @observable fetched_quiz_objects: Array<FetchedQuizObject> = [];
    @observable fetch_in_progress: { [key: string]: boolean } = {};

    rootStore: any;

    constructor(rootStore: any) {
        this.rootStore = rootStore;
        makeObservable(this);
    }

    @action.bound
    fetch_quiz_objects() {
        if (!this.rootStore.profileStore.authTokenSet()) {
            return;
        }

        this.state = "pending";
    }

    @action.bound
    save(quiz_object: QuizObject, cb: Function) {
        var store = this;
        axios()
            .put(`/quiz_objects/${quiz_object._id}`, quiz_object)
            .then(function(res: any) {
                store.quiz_objects.set(
                    quiz_object._id,
                    new QuizObject(quiz_object._id, res.data.quiz_object)
                );
                cb(false, res.data.data);
            })
            .catch(function(err: any) {
                const response = err?.response?.data;
                const firstValidationError = response?.errors &&
                    Object.values(response.errors).flat()[0];
                const message = String(response?.duplicate == "title"
                    ? "A card with that title already exists."
                    : response?.auth
                        ? "You do not have access to edit this card."
                        : response?.error || response?.detail || firstValidationError ||
                            "Could not save this card.");

                store.rootStore.uiStore.flashMessage = message;
                cb(true, message);
            });
    }

    @action.bound
    createFromQuizObject(quiz_object: any, cb: Function) {
        var store = this;

        return axios()
            .post(
                `/subjects/${quiz_object.subject.user}/${quiz_object.subject.slug}/objects`,
                {
                    title: quiz_object.title
                }
            )
            .then(function(res: any) {
                let new_quiz_obj = res.data.quiz_object;
                quiz_object._id = res.data.quiz_object._id;
                if (cb) cb(false, new_quiz_obj);
            })
            .catch(function(err: any) {
                store.rootStore.uiStore.flashMessage = err;
                if (err.response) {
                    if (err.response.data.duplicate == "title") {
                        cb(false, "emailError", "Object Title Already Taken");
                    }
                    if (err.response.data.auth) {
                        cb(false, "authError", "Can't edit that subject.");
                    }
                }
                if (cb) cb(true, {});
            });
    }

    @action.bound
    create(
        subject_user: string,
        subject_slug: string,
        title: string,
        cb: Function
    ) {
        var store = this;
        return axios()
            .post(`/subjects/${subject_user}/${subject_slug}/objects`, {
                title: title
            })
            .then(function(res: any) {
                let new_quiz_obj = res.data.quiz_object;
                store.rootStore.subjectStore.addObject(
                    subject_user,
                    subject_slug,
                    new_quiz_obj
                );

                store.quiz_objects.set(
                    new_quiz_obj._id,
                    new QuizObject(
                      store.rootStore, new_quiz_obj.slug, new_quiz_obj)
                );
                if (cb) cb(false, new_quiz_obj);
            })
            .catch(function(err: any) {
                store.rootStore.uiStore.flashMessage = err;
                if (err.response) {
                    if (err.response.data.duplicate == "title") {
                        cb(false, "emailError", "Object Title Already Taken");
                    }
                    if (err.response.data.auth) {
                        cb(false, "authError", "Can't edit that subject.");
                    }
                }
                if (cb) cb(true, {});
            });
    }

    @action
    delete(quizObjectToDelete: any, cb: Function) {
        var store = this;
        axios()
            .delete(`/quiz_objects/${quizObjectToDelete._id}`)
            .then(function(res: any) {
                let tempMap = new Map();
                for (let quizObject of store.quiz_objects) {
                    if (quizObject[0] !== quizObjectToDelete._id) {
                        tempMap.set(quizObject[0], quizObject[1]);
                    }
                }

                store.quiz_objects = tempMap;
                cb(false, res.data.quiz_object);
            })
            .catch(function(err: any) {
                if (err) {
                    console.log(err);
                }
                store.rootStore.uiStore.flashMessage = err;
                cb(true);
            });
    }

    @action
    addFact(object_id: string) {
        let object = this.quiz_objects.get(object_id);
        object.addFact();
    }

    @action
    setFacts(object_id: string, facts: Array<any>) {
        console.log("Setting Facts to ");
        console.log(facts);
        let object = this.quiz_objects.get(object_id);
        object.facts = facts;
        this.quiz_objects.set(object_id, object);
    }

    updateFact(object_id: string, factID: any, factOrder: any, toSet: any, value: any) {
        this.quiz_objects
            .get(object_id)
            .setFactByIDOrOrder(factID, factOrder, toSet, value);
    }

    @action
    addList(object_id: string) {
        let object = this.quiz_objects.get(object_id);
        object.addList();
    }

    @action
    setList(object_id: string, lists: Array<any>) {
        let object = this.quiz_objects.get(object_id);
        object.assignLists(lists);
        this.quiz_objects.set(object_id, object);
    }

    @action
    addTable(object_id: string) {
        let object = this.quiz_objects.get(object_id);
        object.tables.push(
            new QuizObjectTable(object.tables.length + 1, object)
        );
        this.quiz_objects.set(object_id, object);
    }

    @action
    setTable(object_id: string, tables: Array<any>) {
        let object = this.quiz_objects.get(object_id);
        object.assignTables(tables);
        this.quiz_objects.set(object_id, object);
    }

    @action
    addCardTable(object_id: string, subject: any) {
        let object = this.quiz_objects.get(object_id);
        object.card_tables.push(
            new QuizObjectCardTable(subject, object_id, object.status)
        );
        this.quiz_objects.set(object_id, object);
    }

    @action
    setCardTable(object_id: string, card_tables: Array<any>) {
        let object = this.quiz_objects.get(object_id);
        object.assignCardTables(card_tables);
        this.quiz_objects.set(object_id, object);
    }

    @action
    setQuizObject(field: string, object_id: string, value: string) {
        let object = this.quiz_objects.get(object_id);
        this.quiz_objects.get(object_id).setField(field, value);
        object[field] = value;
        this.quiz_objects.set(object_id, object);
    }

    addNewEmptyObject(subject: any) {
        let uuid = uuidv4();

        let newQuizObject = new QuizObject(
          this.rootStore, uuid, {
            subject: subject,
            new: true
        });

        console.log("Before add new empty Object");
        console.log(this.quiz_objects);
        this.quiz_objects.set(uuid, newQuizObject);

        this.rootStore.subjectStore.addObject(
            subject.user,
            subject.slug,
            newQuizObject,
            uuid
        );
    }

    fetchOne(user: string, subject: string, quiz_object_slug: string) {
        var subject_hash_key: string = `${user}/${subject}`;
        if (this.fetch_in_progress[quiz_object_slug]) {
            return false;
        }

        this.fetch_in_progress[quiz_object_slug] = true;

        var store = this;

        var fetched_quiz_object_list = this.fetched_quiz_objects.map(function(
            fetched_quiz_object
        ) {
            return fetched_quiz_object.slug;
        });

        if (fetched_quiz_object_list.includes(quiz_object_slug)) {
            return false;
        }

        axios()
            .get(`/subjects/${subject_hash_key}/objects/${quiz_object_slug}`)
            .then(function(res: any) {
                if (res.status === 403) {
                    window.location.href = "/login";
                }

                return res.data;
            })
            .then(function(body: any) {
                store.fetch_in_progress[quiz_object_slug] = false;
                store.quiz_objects.set(
                    body._id,
                    new QuizObject(
                        quiz_object_slug, body)
                );
                store.fetched_quiz_objects.push({
                    slug: quiz_object_slug,
                    updated: Date.now()
                });
            })
            .catch(function(err: any) {
                console.log(err);
                store.fetch_in_progress[quiz_object_slug] = false;
                // NotificationManager.warning(
                //     "Click to Dismiss",
                //     "There was a problem retrieving this quiz_object.",
                //     5000
                // );
                store.state = "error";
            });
    }

    fetchByID(quiz_object_id: string) {
        if (this.fetch_in_progress[quiz_object_id]) {
            return false;
        }

        this.fetch_in_progress[quiz_object_id] = true;

        var store = this;

        axios()
            .get(`/quiz_objects/${quiz_object_id}`)
            .then(function(res: any) {
                if (res.status === 403) {
                    window.location.href = "/login";
                }

                return res.data;
            })
            .then(function(body: any) {
                runInAction(() => {
                    store.quiz_objects.set(
                        quiz_object_id,
                        new QuizObject(
                            quiz_object_id,
                            body.quiz_object,
                            body.tracking
                        )
                    );
                    store.fetched_quiz_objects.push({
                        slug: quiz_object_id,
                        updated: Date.now()
                    });
                    store.fetch_in_progress[quiz_object_id] = false;
                });
            })
            .catch(function(err: any) {
                store.fetch_in_progress[quiz_object_id] = false;
                // NotificationManager.warning(
                //     "Click to Dismiss",
                //     "There was a problem retrieving this quiz_object.",
                //     5000
                // );
                store.state = "error";
            });
    }

    get_current_quiz_object() {
        var quiz_object_slug = this.rootStore.uiStore.quiz_object_detail_slug;

        return this.quiz_objects.get(quiz_object_slug);
    }
}
